import React, { useState } from 'react'
import { useNavigate } from 'react-router'
import { AccountHeader } from '@mobi/component-library/Areas/Account'
import { AlertBox } from '@mobi/component-library/Overlays/AlertBox'
import { Button } from '@mobi/component-library/Base/Buttons'
import { Input } from '@mobi/component-library/Selection&Input/Inputs'
import { InfoBox } from '@mobi/component-library/Feedback/InfoBox'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { DocumentPermission } from '@mobi/account/Areas/SignUp/Components/Verification'
import { consentText } from '@mobi/account/Areas/SignUp/Components/Message'
import { DocumentBodyStyled, FormLayout } from './Layout.styled'
import { spacing } from '@mobi/component-library/Foundations'
import { statesInAustralia } from '@mobi/settings/statesInAustralia'
import { DropdownInput } from '@mobi/component-library/Selection&Input/DropdownInput'
import {
  ALPHANUMERIC_REGEX,
  getRequiredValidationMessage,
  isValidName,
  isVerificationSuccessful,
  routes,
} from '@mobi/account/Areas/SignUp/Utils'
import {
  type TypeDriversLicenceDetailsRequest,
  type TypeSignUpApplicationResponse,
  useApplication,
  useVerifyDriversLicence,
} from '../../Hooks'
import { useDispatch, useSelector } from 'react-redux'
import {
  getSignUpState,
  setIsApplicationPendingReview,
  setNumberOfAttemptsToVerify,
  setSignupApplication,
  setSignupVerification,
} from '../../Store'
import { SignupError } from '../SignupError'
import { trackVerificationSuccess } from '@mobi/account/Areas/Verification/Utils/ga'
import { unwrapErrorMessage } from '@mobi/utils/errors'
import { NameInput } from '@mobi/account/Common'
import cardNumberInfoImage from '../../assets/card-number-info.png'
import { useApplicationSubmit } from '../../Hooks/useApplicationSubmit'
import { signupTracker } from '@mobi/account/Common/Analytics'

export const DriversLicence = () => {
  const [showCardNumberInfoModal, setShowCardNumberInfoModal] = useState(false)
  const [givePermission, setGivePermission] = useState(false)
  const [validationMessages, setValidationMessages] = useState<Record<string, string>>({})
  const [showErrorPopup, setShowErrorPopup] = React.useState(false)

  const [state, setState] = React.useState<string | null>('')
  const [number, setNumber] = React.useState('')
  const [cardNumber, setCardNumber] = React.useState('')

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const signUpState = useSelector(getSignUpState)
  const application = signUpState.signupApplication
  const verification = signUpState.signupVerification
  const { verifyDriversLicence, verifyDriversLicenceStatus } = useVerifyDriversLicence()
  const { applicationSave, applicationSaveStatus } = useApplication({
    setValidationMessages,
    setShowErrorPopup,
  })
  const { applicationSubmit, applicationSubmitStatus } = useApplicationSubmit(
    application.applicationReferenceNumber
  )

  const isBusy =
    verifyDriversLicenceStatus === 'pending' ||
    applicationSaveStatus === 'pending' ||
    applicationSubmitStatus === 'pending'

  const allowToProceed =
    givePermission &&
    !!state &&
    !!number &&
    !!cardNumber &&
    isValidName(application.firstName) &&
    isValidName(application.middleName, true) &&
    isValidName(application.surname)

  const onSubmit = () => {
    if (!allowToProceed) {
      return
    }

    dispatch(setNumberOfAttemptsToVerify())

    if (!signUpState.isNewSignupFlowEnabled) {
      const driversLicence: TypeDriversLicenceDetailsRequest = {
        birthdate: application.birthdate,
        firstName: application.firstName,
        surname: application.surname,
        middleName: application.middleName,
        firstNameAlias: application.firstNameAlias,
        lastNameAlias: application.lastNameAlias,
        email: application.email,
        mobileNumber: application.mobileNumber,
        residentialAddress: application.residentialAddress,
        previousAddress: application.previousAddress,
        enquiryId: application.enquiryId,
        applicationReferenceNumber: application.applicationReferenceNumber,
        licenceDetails: {
          number,
          state,
          cardNumber,
        },
        marketingOptInIndicator: application.marketingOptInIndicator,
        termsAndConditionsText: consentText,
        termsAndConditionsConsent: givePermission,
      }
      verifyDriversLicence(driversLicence, {
        onSuccess: verification => {
          if (application.applicationReferenceNumber != null) {
            const verificationResponse: TypeSignUpApplicationResponse = {
              applicationReferenceNumber: application.applicationReferenceNumber,
              enquiryId: verification.enquiryId,
              duplicateExists: false,
              isDuplicateClosed: false,
              isVerified: verification.isVerified,
              unVerifiedReason: verification.unVerifiedReason,
              potentialFraudMatch: verification.potentialFraudMatch,
              potentialSanctionedMatch: verification.potentialSanctionedMatch,
            }
            dispatch(setSignupVerification(verificationResponse))
            const isSuccessful = isVerificationSuccessful(verificationResponse)

            if (isSuccessful) {
              trackVerificationSuccess('Verified Identification', 'licence')
              navigate(routes.password)
              return
            }

            if (
              verificationResponse.duplicateExists ||
              verificationResponse.potentialSanctionedMatch ||
              !verificationResponse.isVerified
            ) {
              navigate(routes.signUpUnsuccessful)
              return
            }
          }
        },
        onError: error => {
          unwrapErrorMessage(error)
          setShowErrorPopup(true)
        },
      })
    }

    if (signUpState.isNewSignupFlowEnabled) {
      applicationSave(
        { driverLicenceDetails: { number, state, cardNumber } },
        {
          onSuccess: res => {
            if (res.duplicateExists) {
              return
            }

            applicationSubmit(
              { termsAndConditionsText: consentText, termsAndConditionsConsent: givePermission },
              {
                onSuccess: res => {
                  dispatch(setSignupApplication({ ...application, enquiryId: res.enquiryId }))
                  dispatch(setIsApplicationPendingReview(res.pendingReview))
                  dispatch(
                    setSignupVerification({
                      ...verification,
                      isVerified: res.isVerified,
                      unVerifiedReason: res.unverifiedReason ?? '',
                    })
                  )
                  if (res.isVerified && !res.pendingReview) {
                    signupTracker({
                      interaction_type: 'ID Verification Success',
                      signUpFlow: signUpState.isVerificationAccount
                        ? 'ID Verification'
                        : 'Standard',
                      attemptNumber: signUpState.numberOfAttemptsToVerify.toString(),
                      optionSelected: 'licence',
                    })
                    navigate(routes.password)
                    return
                  }
                  navigate(routes.signUpUnsuccessful)
                },
                onError: () => {
                  setShowErrorPopup(true)
                },
              }
            )
          },
        }
      )
    }
  }

  const resetFieldValidation = (field: keyof typeof validationMessages) =>
    setValidationMessages(prev => ({
      ...prev,
      [field]: '',
    }))

  if (!(application.applicationReferenceNumber > 0)) {
    return null
  }

  return (
    <>
      <FormLayout>
        <AccountHeader
          alignItems='start'
          title='Enter your Driver’s Licence details'
          description={
            <InfoBox color='blue' size='sm' hideIcon={true}>
              Enter your details as they appear on your ID document.
            </InfoBox>
          }
          style={{ marginBottom: 0 }}
        />
        <DocumentBodyStyled>
          <NameInput
            firstName={application.firstName}
            middleName={application.middleName}
            lastName={application.surname}
            setFirstName={firstName =>
              dispatch(setSignupApplication({ ...application, firstName }))
            }
            setMiddleName={middleName =>
              dispatch(setSignupApplication({ ...application, middleName }))
            }
            setLastName={surname => dispatch(setSignupApplication({ ...application, surname }))}
            isDisabled={isBusy}
          />
          <DropdownInput
            name='state'
            label='State'
            placeholder='Details must match your ID'
            value={state || ''}
            isDisabled={isBusy}
            onChange={event => {
              setState(event.target.value || '')
              resetFieldValidation('state')
            }}
            onBlur={() => {
              setValidationMessages(prev => ({
                ...prev,
                state: getRequiredValidationMessage(state),
              }))
            }}
            isInvalid={!!validationMessages.state}
            helpText={validationMessages.state}
          >
            {statesInAustralia.map(state => (
              <option key={state.value} value={state.value}>
                {state.label}
              </option>
            ))}
          </DropdownInput>
          <Input
            name='licenceNumber'
            label={`Driver's licence number`}
            placeholder='Details must match your ID'
            isDisabled={isBusy}
            value={number}
            onChange={e => setNumber(e.target.value.replace(ALPHANUMERIC_REGEX, ''))}
            onFocus={() => resetFieldValidation('licenceNumber')}
            onBlur={() => {
              setValidationMessages(prev => ({
                ...prev,
                licenceNumber: getRequiredValidationMessage(number),
              }))
            }}
            isInvalid={!!validationMessages.licenceNumber}
            helpText={validationMessages.licenceNumber}
          />
          <Input
            name='cardNumber'
            label={`Driver's licence card number`}
            placeholder='Details must match your ID'
            isDisabled={isBusy}
            value={cardNumber}
            onChange={e => setCardNumber(e.target.value.replace(ALPHANUMERIC_REGEX, ''))}
            onFocus={() => resetFieldValidation('cardNumber')}
            onBlur={() => {
              setValidationMessages(prev => ({
                ...prev,
                cardNumber: getRequiredValidationMessage(cardNumber),
              }))
            }}
            isInvalid={!!validationMessages.cardNumber}
            helpText={validationMessages.cardNumber}
            suffix={
              <Icon
                name='LineInfoCircle'
                size='2rem'
                onClick={() => {
                  signupTracker({
                    interaction_type: 'Drivers License Info',
                    signUpFlow: signUpState.isVerificationAccount ? 'ID Verification' : 'Standard',
                  })
                  setShowCardNumberInfoModal(true)
                }}
                data-testid='drivers-licence-info'
              />
            }
          />
          <div style={{ paddingTop: spacing.smx1 }}>
            <DocumentPermission
              isBusy={isBusy}
              onChange={e => setGivePermission(e.target.checked)}
            />
          </div>
        </DocumentBodyStyled>
        <footer>
          <Button
            isFullWidth
            disabled={!allowToProceed}
            isLoading={isBusy}
            onClick={() => onSubmit()}
          >
            Continue
          </Button>
        </footer>
        <AlertBox
          title='Driver’s Licence Card Number'
          text="A 'card number' is now a mandatory verification field for Australian driver's licences. Depending on your state, this may be found on either the front or back of the card."
          confirmText='Close'
          confirmColor='primary'
          isOpen={showCardNumberInfoModal}
          onConfirm={() => setShowCardNumberInfoModal(false)}
          onCancel={() => setShowCardNumberInfoModal(false)}
          imageCover={<img src={cardNumberInfoImage} alt='Card Number Info' />}
          alignLeft
        />
      </FormLayout>
      <SignupError isOpen={showErrorPopup} onClose={() => setShowErrorPopup(false)} />
    </>
  )
}
